import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Public.css";
import Footer from "../../components/Footer";
import Header from "../../components/Header";
import useSeo from "../../hooks/useSeo";

const pubUrl = process.env.PUBLIC_URL || "";

export default function Residential() {
  useSeo({
    title: "Residential Roofing | Crowned Roofing (Toronto, ON)",
    description:
      "Residential roofing in Toronto and surrounding Ontario communities with straightforward scopes, quality workmanship, and clean jobsite execution from repairs to full replacements.",
    ogImage: "https://www.crownedroofing.com/images/ogimg.png",
    canonical: "https://www.crownedroofing.com/residential",
  });

  const initialLarge = `${pubUrl}/images/residential-img1.png`;
  const initialThumbs = [
    `${pubUrl}/images/residential-img2.png`,
    `${pubUrl}/images/residential-img3.png`,
    `${pubUrl}/images/residential-img4.png`,
  ];

  const [largeImg, setLargeImg] = useState(initialLarge);
  const [thumbs, setThumbs] = useState(initialThumbs);
  const navigate = useNavigate();

  const swapWithLarge = (thumbIndex) => {
    setThumbs((prev) => {
      const next = [...prev];
      const clicked = next[thumbIndex];
      next[thumbIndex] = largeImg;
      setLargeImg(clicked);
      return next;
    });
  };

  useEffect(() => {
    document.title = "Residential Roofing | Crowned Roofing";
    const meta = document.querySelector("meta[name='description']");
    if (meta) {
      meta.setAttribute(
        "content",
        "Residential roofing repairs and replacements built around practical decision-making, clean execution, and straightforward communication."
      );
    }
  }, []);

  return (
    <main className="publicPage">
      <Header />

      <section className="rHero">
        <div
          className="rHeroBg"
          aria-hidden="true"
          style={{ backgroundImage: `url(${pubUrl}/images/residential-hero.png)` }}
        />

        <div className="rHeroInner">
          <div className="rHeroPanel">
            <h1>Residential Roofing, Handled Properly</h1>
            <p>
              Crowned Roofing installs and repairs residential roofs with a focus on workmanship,
              appropriate materials, and clean execution so homeowners can stop worrying about
              what is overhead.
            </p>
            <button className="rBtn" onClick={() => navigate("/contact")}>
              GET A FREE ESTIMATE
            </button>
            <button className="rLink rHeroLink" onClick={() => navigate("/residential/roof-visualizer")}>
              Try the roof visualizer
            </button>
          </div>
        </div>
      </section>

      <section className="rSection rIntro">
        <div className="rIntroGrid">
          <div className="rIntroText">
            <h2>
              <span className="rAccent" aria-hidden="true" />
              Get it done right
            </h2>

            <p>
              Most homeowners do not want options. They want a roof that performs, looks right,
              and does not become a problem later.
            </p>

            <p>
              We approach residential roofing with practical decision-making, experienced crews,
              and attention to the details that actually matter, not inflated promises or rushed installs.
            </p>

            <div className="rMiniRow" aria-label="Project photos">
              {thumbs.map((src, idx) => (
                <button
                  key={src}
                  type="button"
                  className="rThumbBtn"
                  onClick={() => swapWithLarge(idx)}
                  aria-label={`Swap photo ${idx + 1} into main view`}
                >
                  <img src={src} alt="" aria-hidden="true" />
                </button>
              ))}
            </div>
          </div>

          <div className="rIntroImage" aria-label="Selected project photo">
            <img src={largeImg} alt="" />
          </div>
        </div>
      </section>

      <section className="rSection rServices">
        <div className="container">
          <h2 className="sectionTitle centered rTitleTight">
            <span className="accentBar" aria-hidden="true" />
            Residential Roofing Services
          </h2>
          <div className="rServicesSub">Every project starts with understanding the roof as it exists today.</div>

          <div className="rServiceGrid">
            <article className="rServiceCard">
              <img src={`${pubUrl}/icons/residential-ico1.png`} alt="" aria-hidden="true" />
              <h3>Roof Replacement</h3>
              <p>
                When replacement makes sense, we install complete roofing systems, not just new shingles.
                Proper underlayment, flashing, ventilation, and fastening are all addressed so the roof
                performs as intended.
              </p>
            </article>

            <article className="rServiceCard">
              <img src={`${pubUrl}/icons/residential-ico2.png`} alt="" aria-hidden="true" />
              <h3>Roof Repairs</h3>
              <p>
                From localized leaks to storm damage, we identify the cause and fix the issue without turning
                small problems into unnecessary replacements.
              </p>
            </article>

            <article className="rServiceCard">
              <img src={`${pubUrl}/icons/residential-ico3.png`} alt="" aria-hidden="true" />
              <h3>Snow &amp; Hail Damage</h3>
              <p>
                Snow and hail can damage roofing systems in ways that are not always obvious from the ground.
                We help homeowners identify what has been affected by winter storms and what is still in good condition.
              </p>
            </article>

            <article className="rServiceCard">
              <img src={`${pubUrl}/icons/residential-ico4.png`} alt="" aria-hidden="true" />
              <h3>Insurance Claims Assistance</h3>
              <p>
                If your roof damage qualifies for coverage, we assist with documentation and communication,
                keeping expectations realistic and the process straightforward.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="rSection rProcess">
        <div className="container">
          <div className="rProcessPanel">
            <div className="rProcessLine" aria-hidden="true" />
            <div className="rSteps">
              <div className="rStep">
                <div className="rDot" aria-hidden="true" />
                <div className="rStepTitle">On-Site Evaluation</div>
                <div className="rStepText">
                  We begin with a visit to assess the roof as it exists today. We look at surface
                  conditions, transitions, penetrations, and visible indicators of underlying issues.
                  Findings are explained clearly and without pressure so you understand what matters,
                  what does not, and what options actually make sense for your home.
                </div>
              </div>

              <div className="rStep">
                <div className="rDot" aria-hidden="true" />
                <div className="rStepTitle">Clear Scope &amp; Pricing</div>
                <div className="rStepText">
                  If work is recommended, you will receive a written scope that defines the materials,
                  labor, and expectations upfront. Pricing is straightforward, line items are clear,
                  and nothing is buried unnecessarily so there are no surprises once the project is underway.
                </div>
              </div>

              <div className="rStep">
                <div className="rDot" aria-hidden="true" />
                <div className="rStepTitle">Professional Installation</div>
                <div className="rStepText">
                  Our crews follow a controlled workflow focused on proper sequencing, clean job sites,
                  and respect for your property. Materials are installed according to manufacturer
                  specifications and industry best practices, with close oversight from start to finish.
                </div>
              </div>

              <div className="rStep">
                <div className="rDot" aria-hidden="true" />
                <div className="rStepTitle">Final Review &amp; Close-Out</div>
                <div className="rStepText">
                  Once the work is complete, we review the project to confirm it meets the agreed scope
                  and internal standards. Details are addressed before close-out, leaving you with a
                  properly installed roof and confidence that the job was finished the right way.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="rFinal">
        <div className="rFinalCard">
          <div className="rFinalTitle">Start With an Inspection</div>
          <div className="rFinalSub">
            If you need clarity on the condition of your roof, we can help you understand your options before any scope is discussed.
          </div>
          <button className="rBtn" onClick={() => navigate("/contact")}>
            GET A FREE ESTIMATE
          </button>
          <button className="rLink" onClick={() => navigate("/residential/roof-visualizer")}>
            Try the roof visualizer
          </button>
          <button className="rLink" onClick={() => navigate("/portfolio")}>
            View a Sample Repair Estimate
          </button>
          <button className="rLink" onClick={() => navigate("/faq/shingle-vs-metal-roof-ontario")}>
            Read the shingle vs. metal FAQ
          </button>
        </div>
      </section>

      <Footer />
    </main>
  );
}
